import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setCurrentOrgIdState] = useState(() => localStorage.getItem("currentOrgId") || null);

  const setCurrentOrgId = useCallback((id) => {
    setCurrentOrgIdState(id);
    if (id) localStorage.setItem("currentOrgId", id);
  }, []);

  const refreshOrgs = useCallback(async () => {
    try {
      const res = await api.get("/orgs");
      setOrgs(res.data);
      return res.data;
    } catch {
      return [];
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      const list = await refreshOrgs();
      setCurrentOrgIdState((prev) => {
        const valid = prev && list.some((o) => o.org_id === prev);
        const next = valid ? prev : (list[0]?.org_id || null);
        if (next) localStorage.setItem("currentOrgId", next);
        return next;
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshOrgs]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.error("Logout request failed", err); }
    setUser(null);
    localStorage.removeItem("currentOrgId");
    window.location.href = "/login";
  }, []);

  const currentOrg = orgs.find((o) => o.org_id === currentOrgId) || null;

  const value = useMemo(() => ({
    user, setUser, loading, checkAuth, logout,
    orgs, refreshOrgs, currentOrg, currentOrgId, setCurrentOrgId,
  }), [user, loading, checkAuth, logout, orgs, refreshOrgs, currentOrg, currentOrgId, setCurrentOrgId]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
